import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { PartnerSchedulingRequestedPartner } from './PartnerSchedulingRequestedPartner.js';
import { A } from '@adornis/base/env-info.js';

export enum PartnerSchedulingTaskStatus {
  WAITING_FOR_PARTNER_RESPONSE = 'WAITING_FOR_PARTNER_RESPONSE',
  PARTNERS_EXHAUSTED = 'PARTNERS_EXHAUSTED',
  PARTNER_SELECTED = 'PARTNER_SELECTED',
  MAIN_TICKET_FE_ASSIGNED = 'MAIN_TICKET_FE_ASSIGNED',
  MAIN_TICKET_FE_DETAILS_UPDATED = 'MAIN_TICKET_FE_DETAILS_UPDATED',
  FE_ACCOUNT_REQUESTED = 'FE_ACCOUNT_REQUESTED',
  FE_PROVIDER_UPDATED = 'FE_PROVIDER_UPDATED',
  CLIENT_NOTIFICATION_SKIPPED = 'CLIENT_NOTIFICATION_SKIPPED',
  CLIENT_DISABLED = 'CLIENT_DISABLED',
  QUEUE_NOT_ALLOWED = 'QUEUE_NOT_ALLOWED',
  CLIENT_NOTIFIED = 'CLIENT_NOTIFIED',
  MANUAL_REVIEW_REQUIRED = 'MANUAL_REVIEW_REQUIRED',
  CLOSED_NO_VALID_RESPONSE = 'CLOSED_NO_VALID_RESPONSE',
}

registerEnum({ enum: PartnerSchedulingTaskStatus, name: 'PartnerSchedulingTaskStatus' });

@Entity()
export class PartnerSchedulingTaskState extends MongoEntity {
  static override _class = 'PartnerSchedulingTaskState';
  static override _collectionName = 'partner-scheduling-task-states';

  static override resolveID(_entity: PartnerSchedulingTaskState): string {
    return (_entity._id ??= A.getGloballyUniqueID());
  }

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  override _id!: string;

  @Field(() => String)
  mainTicketId: Maybe<string>;

  @Field(() => [String])
  processedArticleIDs: Maybe<string[]>;

  @Field(() => String)
  mainTicketNumber: Maybe<string>;

  @Field(() => String)
  clientName: Maybe<string>;

  @Field(() => String)
  region: Maybe<string>;

  @Field(() => String)
  country: Maybe<string>;

  @Field(() => String)
  city: Maybe<string>;

  @Field(() => String)
  address: Maybe<string>;

  @Field(() => String)
  zipCode: Maybe<string>;

  @Field(() => String)
  subticketId: Maybe<string>;

  @Field(() => Date)
  subticketCreatedAt: Maybe<Date>;

  @Field(() => String)
  subticketNumber: Maybe<string>;

  @Field(() => [PartnerSchedulingRequestedPartner], { default: v => v ?? [] })
  requestedPartners: Maybe<PartnerSchedulingRequestedPartner[]>;

  @Field(() => Int, { default: v => v ?? 0 })
  currentBatchIndex: Maybe<number>;

  @Field(() => String)
  slaMode: Maybe<string>;

  @Field(() => Date)
  lastRequestSentAt: Maybe<Date>;

  @Field(() => PartnerSchedulingTaskStatus)
  status: Maybe<PartnerSchedulingTaskStatus>;

  @Field(() => String)
  selectedPartnerName: Maybe<string>;

  @Field(() => String)
  selectedPartnerEmail: Maybe<string>;

  @Field(() => Date)
  selectedEtaUtc: Maybe<Date>;

  @Field(() => String)
  selectedTravelCost: Maybe<string>;

  @Field(() => String)
  selectedFeName: Maybe<string>;

  @Field(() => String)
  selectedFePhone: Maybe<string>;

  @Field(() => String)
  selectedFeEmail: Maybe<string>;

  @Field(() => Date)
  decisionAt: Maybe<Date>;

  @Field(() => [String], { default: v => v ?? [] })
  processedResponseArticleIds: Maybe<string[]>;

  @Field(() => Date)
  escalatedAt: Maybe<Date>;

  @Field(() => Date)
  acceptedAt: Maybe<Date>;

  @Field(() => Date)
  losersNotifiedAt: Maybe<Date>;

  @Field(() => Date)
  feAssignedStatusAt: Maybe<Date>;

  @Field(() => Date)
  feDetailsUpdatedAt: Maybe<Date>;

  @Field(() => Date)
  feAccountRequestedAt: Maybe<Date>;

  @Field(() => Date)
  feProviderUpdatedAt: Maybe<Date>;

  @Field(() => Date)
  clientNotifiedAt: Maybe<Date>;

  @Field(() => Date)
  clientNotificationSkippedAt: Maybe<Date>;
}
